import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { calculateShipping, calculateTax } from '../utils/cart';
import '../styles/Checkout.css';

const priceOf = item =>
  typeof item.price === 'string' ? parseFloat(item.price.replace('$', '')) : item.price;

const Checkout = ({ cartItems = [], onOrderPlaced }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [promoCode, setPromoCode] = useState(location.state?.promoCode || '');
  const [discount, setDiscount] = useState(0);
  const [promoMessage, setPromoMessage] = useState('');
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.username || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    paymentMethod: 'credit',
  });

  const subtotal = cartItems.reduce((total, item) => total + priceOf(item) * item.quantity, 0);
  const shipping = cartItems.length ? calculateShipping(cartItems) : 0;
  const tax = calculateTax(Math.max(subtotal - discount, 0));
  const total = Math.max(subtotal - discount, 0) + shipping + tax;

  const handleChange = event => {
    setFormData(current => ({ ...current, [event.target.name]: event.target.value }));
  };

  const applyPromo = async () => {
    setPromoMessage('');
    if (!promoCode.trim()) {
      setDiscount(0);
      return;
    }
    const response = await fetch('/api/promotions/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code: promoCode,
        subtotal,
        items: cartItems.map(item => ({
          price: priceOf(item),
          quantity: item.quantity,
          category: item.category,
        })),
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      setDiscount(0);
      throw new Error(data.message || 'Invalid promo code');
    }
    setDiscount(data.discount || 0);
    setPromoCode(data.code || promoCode);
    setPromoMessage(data.message);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');
    if (!cartItems.length) {
      setError('Your cart is empty.');
      return;
    }

    setIsLoading(true);
    try {
      if (promoCode.trim()) {
        await applyPromo();
      }

      const token = localStorage.getItem('token');
      const response = await fetch('/api/orders/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          ...formData,
          promoCode,
          items: cartItems.map(item => ({
            id: item.id,
            quantity: item.quantity,
            size: item.size,
            color: item.color,
          })),
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to place the order');
      }
      setOrder(data.order);
      onOrderPlaced?.();
    } catch (err) {
      setError(err.message || 'Failed to process order. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (order) {
    return (
      <div className='checkout-container'>
        <h1>Order confirmed</h1>
        <div className='order-summary'>
          <p>Thank you, {order.customer_name}. Your order {order.order_number} is pending.</p>
          <p>A copy of the details was saved{user ? ' to your account' : ''}.</p>
          <p className='summary-total'>Total: ${order.total.toFixed(2)}</p>
          <Link to={user ? '/account?tab=orders' : '/shop'} className='submit-btn checkout-link'>
            {user ? 'View orders' : 'Continue shopping'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className='checkout-container'>
      <h1>Checkout</h1>
      {!cartItems.length ? (
        <div className='order-summary'>
          <p>Your cart is empty.</p>
          <Link to='/shop'>Return to the shop</Link>
        </div>
      ) : (
        <div className='checkout-content'>
          <div className='order-summary'>
            <h2>Order Summary</h2>
            <ul className='checkout-items'>
              {cartItems.map(item => (
                <li key={item.lineId || item.id}>
                  <span>
                    {item.name}
                    {item.size ? ` / ${item.size}` : ''}
                    {item.color ? ` / ${item.color}` : ''} × {item.quantity}
                  </span>
                  <span>${(priceOf(item) * item.quantity).toFixed(2)}</span>
                </li>
              ))}
            </ul>
            <p>Subtotal: ${subtotal.toFixed(2)}</p>
            <p>Shipping: {shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</p>
            <p>Tax: ${tax.toFixed(2)}</p>
            {discount > 0 && <p>Discount: -${discount.toFixed(2)}</p>}
            <p className='summary-total'>Total: ${total.toFixed(2)}</p>
            <p className='shipping-note'>Free shipping on orders over $100.</p>
          </div>
          <form className='checkout-form' onSubmit={handleSubmit}>
            <div className='form-group'>
              <label htmlFor='name'>Full Name</label>
              <input id='name' name='name' value={formData.name} onChange={handleChange} required />
            </div>
            <div className='form-group'>
              <label htmlFor='email'>Email</label>
              <input
                type='email'
                id='email'
                name='email'
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className='form-group'>
              <label htmlFor='phone'>Phone</label>
              <input id='phone' name='phone' value={formData.phone} onChange={handleChange} />
            </div>
            <div className='form-group'>
              <label htmlFor='address'>Shipping Address</label>
              <textarea
                id='address'
                name='address'
                value={formData.address}
                onChange={handleChange}
                required
              />
            </div>
            <div className='form-row'>
              <div className='form-group'>
                <label htmlFor='city'>City</label>
                <input id='city' name='city' value={formData.city} onChange={handleChange} required />
              </div>
              <div className='form-group'>
                <label htmlFor='state'>State</label>
                <input id='state' name='state' value={formData.state} onChange={handleChange} />
              </div>
            </div>
            <div className='form-row'>
              <div className='form-group'>
                <label htmlFor='zipCode'>ZIP Code</label>
                <input
                  id='zipCode'
                  name='zipCode'
                  value={formData.zipCode}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className='form-group'>
                <label htmlFor='country'>Country</label>
                <input
                  id='country'
                  name='country'
                  value={formData.country}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className='form-group'>
              <label htmlFor='promo'>Promo code</label>
              <div className='promo-row'>
                <input
                  id='promo'
                  value={promoCode}
                  onChange={event => setPromoCode(event.target.value)}
                />
                <button type='button' onClick={() => applyPromo().catch(err => setError(err.message))}>
                  Apply
                </button>
              </div>
              {promoMessage && <p>{promoMessage}</p>}
            </div>
            <div className='form-group'>
              <label>Payment Method</label>
              <div className='payment-options'>
                <label>
                  <input
                    type='radio'
                    name='paymentMethod'
                    value='credit'
                    checked={formData.paymentMethod === 'credit'}
                    onChange={handleChange}
                  />
                  Credit Card
                </label>
                <label>
                  <input
                    type='radio'
                    name='paymentMethod'
                    value='paypal'
                    checked={formData.paymentMethod === 'paypal'}
                    onChange={handleChange}
                  />
                  PayPal
                </label>
              </div>
              <p className='shipping-note'>Payment is recorded with the order. No card is charged in this demo.</p>
            </div>
            {error && <p className='form-error'>{error}</p>}
            <button type='submit' className='submit-btn' disabled={isLoading}>
              {isLoading ? 'Processing...' : `Place Order · $${total.toFixed(2)}`}
            </button>
            {!user && (
              <p>
                Checking out as a guest. <Link to='/auth?mode=login'>Log in</Link> to save this order
                to your account.
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  );
};

export default Checkout;
